import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Steps,
  Divider,
  Form,
  Alert,
  FormItemProps,
} from 'antd';

import { Rule } from 'antd/lib/form';
import ActionsItems from '@components/Actions/ActionsItems';
import { FormInstance } from 'antd/es/form/Form';
import ActionTypes from '@utils/deviceActions';
import SessionContext from '@providers/Session/SessionContext';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { DeviceActionStepsProps, DeviceActionProps } from './types';
import useSimcardImportStep from './useSimcardImportStep';
import * as S from './styles';

const { Item } = Form;

export interface ActionsModalProps {
  isOpen: boolean;
  onClose: (isOpen: false) => void;
  onConfirm: () => void;
  onConfirmLoading: boolean;
  selectedAction: DeviceActionProps | undefined;
  isUpload?: boolean;
  form: FormInstance<any> | undefined;
}

const ActionsModal = ({
  isOpen,
  onClose,
  onConfirm,
  onConfirmLoading,
  selectedAction,
  form,
  isUpload,
}: ActionsModalProps) => {
  const { t } = useTranslation('device-actions-modal');
  const { selectedApp } = useContext(SessionContext);
  const formValues = Form.useWatch([], form);

  const numberOfDevices = useMemo(
    () => selectedAction?.payloadToSend?.devices?.length,
    [selectedAction?.payloadToSend?.devices?.length],
  );

  const currentStep = useMemo(
    () =>
      formValues
        ? Object.values(formValues)?.filter(
            value => value || value === 0 || value === false,
          )?.length
        : 0,
    [formValues],
  );

  const handleChangePositionUploadStep = useCallback(
    (steps: DeviceActionStepsProps[], newPosition: number) => {
      const uploadIndex = 0;
      const listItem = steps.splice(uploadIndex, 1)[0];

      steps.splice(newPosition, 0, listItem);
      return steps;
    },
    [],
  );

  const isUploadDisabled = useMemo(
    (): boolean =>
      selectedAction?.uploadPosition
        ? currentStep < selectedAction?.uploadPosition
        : false,
    [currentStep, selectedAction?.uploadPosition],
  );

  // reset of items that come after what was changed
  const handleItemChange = useCallback(
    (changedItem: { [key: string]: any }) => {
      const newFormValues = { ...form?.getFieldsValue() };
      const keys = Object.keys(newFormValues);
      const changedItemKey = Object.keys(changedItem)[0];
      const changedKeyIndex = keys.indexOf(changedItemKey);

      if (changedKeyIndex >= 0 && changedKeyIndex < keys.length) {
        keys.forEach((key: string, indexKey: number) => {
          const preserveStep = selectedAction?.steps?.[indexKey]?.preserveStep;
          if (indexKey > changedKeyIndex && !preserveStep) {
            form?.resetFields([key]);
          }
        });
      }
    },
    [form, selectedAction?.steps],
  );

  const handleUploadRemove = useCallback(
    () => handleItemChange({ uploadedItems: undefined }),
    [handleItemChange],
  );

  const isLoraWan = useMemo(() => {
    const isProductWithLorawan = [
      ApplicationsConstants.LORAWAN,
      ApplicationsConstants.MOT_ENTERPRISE,
      ApplicationsConstants.MOT_BUSINESS,
    ].includes(selectedApp as ApplicationsConstants);
    const hasLorawanInPathname = window.location.pathname.includes('lorawan');

    return isProductWithLorawan && hasLorawanInPathname;
  }, [selectedApp]);

  const uploadStep = ActionsItems.Common.useUpload({
    actionKey:
      selectedAction?.payloadToSend?.action ?? selectedAction?.actionKey,
    form,
    customInfo: selectedAction?.uploadCustomInfo,
    disabled: isUploadDisabled,
    onRemove: handleUploadRemove,
    isLoraWan,
  });

  const simcardImportStep = useSimcardImportStep(
    selectedAction?.payloadToSend?.action,
    form,
    uploadStep?.content,
    isLoraWan,
  );

  const actionSteps: DeviceActionStepsProps[] = useMemo(() => {
    let steps = selectedAction?.steps ?? [];
    if (isUpload && !selectedAction?.disableDefaultUpload) {
      const actionKey = selectedAction?.payloadToSend?.action;
      const isActivation =
        actionKey === ActionTypes.ACTIVATION_MULTI_CONNECT ||
        actionKey === ActionTypes.ACTIVATION_MOT;
      const stepsWithUpload = [...steps.filter(step => step?.showWhenUpload)];
      stepsWithUpload.unshift(isActivation ? simcardImportStep : uploadStep);

      steps = selectedAction?.uploadPosition
        ? handleChangePositionUploadStep(
            stepsWithUpload,
            selectedAction?.uploadPosition,
          )
        : stepsWithUpload;
    }
    return steps;
  }, [
    selectedAction?.steps,
    selectedAction?.disableDefaultUpload,
    selectedAction?.payloadToSend?.action,
    selectedAction?.uploadPosition,
    isUpload,
    simcardImportStep,
    uploadStep,
    handleChangePositionUploadStep,
  ]);

  const handleOnFinish = useCallback(() => {
    form?.validateFields().then(() => {
      onConfirm();
    });
  }, [form, onConfirm]);

  const handleCancel = useCallback(() => {
    form?.resetFields();
    onClose(false);
  }, [form, onClose]);

  const stepsItems = useMemo(
    () =>
      actionSteps?.map(stepItem => {
        const rules: Rule[] = stepItem?.isNotRequired
          ? []
          : [
              {
                required: true,
                message: `* ${t('msgs.required-filed')}`,
              },
            ];

        if (stepItem?.rules) {
          rules.push(...stepItem?.rules);
        }

        if (stepItem?.initialValue && !form?.getFieldValue(stepItem?.name)) {
          form?.setFieldValue(stepItem?.name, stepItem?.initialValue);
        }

        const itemProps: FormItemProps = {
          name: stepItem?.name,
          valuePropName: stepItem?.valuePropName,
          rules,
        };

        if (stepItem?.disabledValueFromEvent) {
          itemProps.getValueFromEvent = e => e?.preventDefault();
        }

        return {
          ...stepItem,
          description: (
            <div style={{ padding: '8px 0', marginBottom: 16, opacity: 0.8 }}>
              {stepItem?.itemInfo && <div>{stepItem?.itemInfo}</div>}
              <Item {...itemProps}>{stepItem?.content}</Item>
              {stepItem?.showItemMessage && (
                <span className="action-modal-item-error">{`* ${stepItem?.itemMessage}`}</span>
              )}
            </div>
          ),
        };
      }),
    [actionSteps, form, t],
  );

  return (
    <Modal
      title={
        selectedAction?.title ??
        t(`device-actions:labels.${selectedAction?.payloadToSend?.action}`)
      }
      open={isOpen}
      onCancel={handleCancel}
      bodyStyle={{ padding: '24px 40px' }}
      width={selectedAction?.modalWidth}
      footer={false}
      destroyOnClose
    >
      <S.ModalContent isUpload={isUpload}>
        {selectedAction?.description && (
          <>
            <div className="action-modal-description-text">
              {selectedAction?.description}
            </div>
            {actionSteps?.length > 0 && <Divider />}
          </>
        )}
        {!isUpload && stepsItems?.length === 0 && (
          <span className="action-modal-confirm-request">
            {t('msgs.confirm-request')}
          </span>
        )}
        <Form
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleItemChange}
        >
          <Steps
            direction="vertical"
            current={currentStep}
            items={stepsItems}
          />
          {selectedAction?.infoMessage && (
            <>
              <Divider />
              <Alert
                description={selectedAction?.infoMessage}
                type="warning"
                showIcon
              />
            </>
          )}
          <Divider />
          <S.ModalFooter totalDevices={numberOfDevices ?? 0}>
            {numberOfDevices ? (
              <span className="action-modal-footer-total-devices">
                {t('selected-device', { count: numberOfDevices })}
              </span>
            ) : undefined}
            <Button
              type="primary"
              htmlType="submit"
              loading={onConfirmLoading}
              disabled={selectedAction?.confirmDisabled}
            >
              {t(onConfirmLoading ? 'sending' : 'confirm')}
            </Button>
          </S.ModalFooter>
        </Form>
      </S.ModalContent>
    </Modal>
  );
};

export default ActionsModal;
